/**
 * Mandantentrennung über die Firma.
 *
 * Historisch hing die Trennung am Freitext `company_name` (getrimmt/kleingeschrieben
 * verglichen). Ein Tippfehler in einem Profil legte den Benutzer damit still in
 * einen eigenen Ein-Personen-Mandanten (Vorfall 2026-08-03). Seit
 * `supabase/companies.sql` trägt jedes Profil eine stabile `company_id`; der Name
 * bleibt reiner Anzeigename (Briefkopf/PDF). Die ID ist ein kurzer Zifferncode
 * (max. 5 Stellen, Konvention: PLZ des Firmensitzes — GefAS = '24632'), hier
 * aber nur als undurchsichtiger String behandelt: verglichen wird auf Gleichheit,
 * nie auf Format.
 *
 * Solange noch Zeilen ohne Backfill existieren — und damit App und SQL in beliebiger
 * Reihenfolge ausgerollt werden dürfen — fällt der Vergleich auf den alten
 * String-Vergleich zurück.
 */

export interface CompanyScoped {
    company_id?: string | null;
    company_name?: string | null;
}

/**
 * Die eine Schreibweisen-Regel fuer Firmennamen: Rand- UND Innenleerraum
 * vereinheitlicht, kleingeschrieben.
 *
 * Der Innenleerraum gehoert dazu, weil doppelte Leerzeichen dieselbe Falle
 * aufstellen wie der Vorfall vom 2026-08-03 — man sieht sie im Eingabefeld
 * nicht und sie trennen den Mandanten trotzdem. Frueher kollabierte nur der
 * Rechnungsmodul-Vergleich sie, dieser hier nicht; zwei Regeln fuer dieselbe
 * Frage sind eine Regel zu viel.
 */
export const normalizeCompanyName = (value?: string | null): string =>
    (value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const normalizeName = normalizeCompanyName;

const idOf = (value?: string | null): string => (value || '').trim();

/**
 * Gehören beide Datensätze zur selben Firma?
 * Beide `company_id` gesetzt → ID-Vergleich (tippfehlerfest).
 * Sonst → normalisierter Namensvergleich wie bisher (Übergangssicherheit).
 */
export const sameCompany = (a?: CompanyScoped | null, b?: CompanyScoped | null): boolean => {
    const idA = idOf(a?.company_id);
    const idB = idOf(b?.company_id);
    if (idA && idB) return idA === idB;
    return normalizeName(a?.company_name) === normalizeName(b?.company_name);
};

export interface CompanyFilter {
    column: 'company_id' | 'company_name';
    value: string;
}

/**
 * Server-seitiger Filter für Supabase-Queries: `company_id`, sobald gesetzt,
 * sonst der Freitext-Name. `null` heißt "kein Filter" — dasselbe Verhalten wie
 * bisher bei leerem `company_name`.
 */
export const companyFilter = (me?: CompanyScoped | null): CompanyFilter | null => {
    const id = idOf(me?.company_id);
    if (id) return { column: 'company_id', value: id };
    const name = me?.company_name;
    if (name) return { column: 'company_name', value: name };
    return null;
};
